import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment/moment';

// component and helper Activities
import { tabs } from './Utils';
import { getDate } from './Analytics/helpers/utils';
import { Dashboard, DashboardContext } from '@acoer/ac-js-lib-charts';
import history from '../../Shared/history';
import FreeTextSearch from '../../Shared/FreeTextSearch';
import { workspaceActions } from '../WorkspaceActions';
import * as dashboardActions from './DashboardActions';
import { config } from './helpers/defaultDashboardConfig';
import ExportPDFIcon from '../../../assets/img/UpdatedPDFIcon.svg';
import ExportPDFIconDarkMode from '../../../assets/img/WhitePDFIconForDarkMode.svg';
import Filters from './Analytics/Filters/Filters';
import { AppContext } from '../../../containers/AppProviderWrapper';
import * as caseActions from '../Case/CaseActions';
import { config as defaultDashboardConfig } from './helpers/defaultDashboardConfig';

// material- ui
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

const operatorMap = {
    is: 'eq',
    'is not': 'ne',
    contains: 'like',
    'greater than or equal to': 'gte',
    'lower than or equal to': 'lte',
    'is one of': 'in',
    'is not one of': 'nin',
};

const getWhere = (initial: any, where: any) =>
    (where &&
        Object.keys(where).reduce((accum, current) => {
            if (Object.keys(accum).includes(current)) {
                accum[current] = {
                    ...accum[current],
                    [operatorMap[where[current].operator] ||
                    where[current].operator]: where[current].values,
                };
                return accum;
            }

            // apply filters of applied saved template
            if (where[current].filters) {
                const nested = where[current].filters;
                return getWhere(initial, nested);
            }

            // Edge case for route to diagnosis pie chart
            if (where[current].id === 'routeToDiagnosis' && where[current].values[0] === 'GP Referral') {
                accum[current] = {
                    type: 'STRING',
                    in: ['GP referral', 'GP Referral'],
                };
                return accum;
            }
            // continue with other types
            if (where[current].id.includes('(')) {
                let str = where[current].id;
                let valueInsideBrackets = str.slice(
                    str.indexOf('(') + 1,
                    str.indexOf(')')
                );
                where[current].id = valueInsideBrackets;
            } else if (
                where[current].type === 'date' ||
                where[current].id === 'createdAt'
            ) {
                if (Array.isArray(where[current].values)) {
                    const startDate = where[current].values[1];
                    accum[current] = {
                        type: 'TEMPORAL',
                        gte: where[current].values[0],
                        lte: moment(startDate).add(1, 'days'),
                    };
                } else {
                    accum[current] = {
                        type: 'TEMPORAL',
                        gte: getDate(where[current].values),
                    };
                }
            } else if (
                where[current].type === 'NUMERICAL' ||
                where[current].type === 'number'
            ) {
                const isArrayOperator = Array.isArray(where[current].operator);
                if (isArrayOperator) {
                    accum[current] = where[current].operator.reduce(
                        (operatorAccum: any, op: any, idx: any) => ({
                            ...operatorAccum,
                            [op]: parseInt(where[current].values[idx]),
                        }),
                        {
                            type: 'NUMERICAL',
                        }
                    );
                } else {
                    // single operator implement greater than or equal to and less than one more
                    accum[current] = {
                        type: 'NUMERICAL',
                        gte: parseInt(where[current].values),
                        lt: parseInt(where[current].values) + 1,
                    };
                }
            } else {
                accum[current] = {
                    type: where[current].type || 'STRING',
                    [operatorMap[where[current].operator] ||
                    where[current].operator]: where[current].values,
                };
            }
            return accum;
        }, initial || {})) ||
    initial;

export const DashboardWrapper = () => {
    // @ts-ignore
    const { view, type, area, tabValue, setTabValue } =
        // @ts-ignore
        useContext(DashboardContext);
    const { workspaceList, currentUser } = useContext(AppContext);
    const [dependentData, setDependentData] = useState({});
    const [dashboardData, setDashboardData] = useState<string>('case');
    const dispatch = useDispatch();
    const location = useLocation();
    const dashboard = useSelector((state: any) => state.dashboard);
    // const areaConfigs = useSelector((state: any) => state.areaConfigs);
    const areaConfigs = {
        [area.toString()]: {
            loading: false,
            'dashboard-configuration': defaultDashboardConfig,
        },
    };
    const dashboardConfig = useSelector((state: any) => state.dashboardConfig);
    const savedFilters = useSelector(
        (state: any) => state.dashboard.filter && state.dashboard.filter.data
    );
    const freeTextSearch = useSelector(
        (state: any) => state.dashboard.quickText
    );
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const userRole = useSelector(
        (state: any) => state.workspace.workspaceUserRole
    );
    const templateUsers = ['ADMIN', 'OWNER'];

    useEffect(() => {
        let revisedData = dashboard;
        if (
            type !== 'activity' &&
            dashboard &&
            dashboard.tableData &&
            dashboard.tableData.items &&
            dashboard.tableData.items.length
        ) {
            revisedData.tableData = {
                ...revisedData.tableData,
                items: dashboard.tableData.items.map((obj, i) => ({
                    ...obj,
                    _id: obj._id.includes('/')
                        ? obj._id.split('/').pop()
                        : obj._id,
                    ...(obj.deathDate && {
                        deathDate: moment.utc(obj.deathDate).format(),
                    }),
                })),
            };
            setDashboardData(revisedData);
        } else if (
            type === 'activity' &&
            dashboard &&
            dashboard.activityTable &&
            dashboard.activityTable.items &&
            dashboard.activityTable.items.length &&
            dashboard.activityByWorkspaceId &&
            dashboard.activityByWorkspaceId.items &&
            dashboard.activityByWorkspaceId.items.length
        ) {
            // replace workspace ID with workspace name in table
            revisedData.activityTable = {
                ...revisedData.activityTable,
                items: dashboard.activityTable.items.map((obj, i) => ({
                    ...obj,
                    workspaceName:
                        workspaceList &&
                        workspaceList.items.find(
                            (o) => o.id === obj.workspaceId
                        )?.name,
                    userId:
                        currentUser &&
                        currentUser.userConfiguration &&
                        currentUser.userConfiguration.beta
                            ? 'betaTester@test.com'
                            : obj.userId,
                })),
            };
            // delete workspaceId column to avoid repetition
            revisedData.activityTable.items.forEach(function (obj) {
                // obj.workspace = obj.workspaceId;
                delete obj.workspaceId;
            });

            // replace workspaceId with workspace name in activity barchart
            // first check if workspaceId is the same as _id to avoid repetition and clear of data
            if (
                dashboard.activityByWorkspaceId.items.length > 0 &&
                dashboard.activityByWorkspaceId.items[0].workspaceId ===
                    dashboard.activityByWorkspaceId.items[0]._id
            ) {
                revisedData.activityByWorkspaceId = {
                    ...revisedData.activityByWorkspaceId,
                    items: dashboard.activityByWorkspaceId.items.map(
                        (obj, i) => ({
                            ...obj,
                            workspaceId:
                                `${
                                    workspaceList &&
                                    workspaceList.items.find(
                                        (o) => o.id === obj.workspaceId
                                    )?.name
                                } (${obj.workspaceId})` || obj.workspaceId,
                        })
                    ),
                };
            }
            setDashboardData(revisedData);
        } else {
            setDashboardData(dashboard);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboard]);

    const handleFreeTextSearchChange = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        dispatch(
            dashboardActions.quickTextSearch(event.target.value as string)
        );
        if (event.target.value === '') {
            retrieveData(
                type,
                area,
                areaConfigs[area]['dashboard-configuration'].tabs[tabValue],
                savedFilters || null,
                ''
            );
        }
    };
    

    const handleQuickSearch = () => {
        retrieveData(
            type,
            area,
            areaConfigs[area]['dashboard-configuration'].tabs[tabValue],
            savedFilters || null,
            freeTextSearch
        );
    };

    const dependentQueries = useCallback(
        (tabConfigs: any) => ({
            meansOfDeathBySex: () =>
                Object.keys(dashboard).reduce(
                    (accum, key) => ({
                        ...accum,
                        [key]:
                            dashboard[key].items.length > 0
                                ? dashboard[key].items.map(
                                      (value: any) =>
                                          value[
                                              tabConfigs.queries[key].groupBy[0]
                                          ]
                                  )
                                : ['none'],
                    }),
                    {}
                ),
            causesOfDeathBySex: () =>
                Object.keys(dashboard).reduce(
                    (accum, key) => ({
                        ...accum,
                        [key]:
                            dashboard[key].items.length > 0
                                ? dashboard[key].items.map(
                                      (value: any) =>
                                          value[
                                              tabConfigs.queries[key].groupBy[0]
                                          ]
                                  )
                                : ['none'],
                    }),
                    {}
                ),
            causeOfDeathByPathologist: () =>
                Object.keys(dashboard).reduce(
                    (accum, key) => ({
                        ...accum,
                        [key]:
                            dashboard[key].items.length > 0
                                ? dashboard[key].items.map(
                                      (value: any) =>
                                          value[
                                              tabConfigs.queries[key].groupBy[0]
                                          ]
                                  )
                                : ['none'],
                    }),
                    {}
                ),
        }),
        [dashboard]
    );

    const retrieveData = useCallback(
        (
            type: string,
            area: string,
            tabConfigs: any,
            filter: any,
            freeTextSearch: string
        ) => {
            const depQuery = tabConfigs.dependentQueries || null;
            let queryProp: any;
            if (type === 'activity') {
                const query = tabConfigs.queries;
                queryProp =
                    query &&
                    query(filter, getWhere, freeTextSearch, currentUser);
            } else {
                queryProp = {};
                let queries = { ...tabConfigs.queries };
                Object.keys(queries).forEach((query) => {
                    const initialValue = queries[query]['initialFilter'];
                    queryProp = {
                        ...queryProp,
                        [query]: {
                            ...queries[query],
                            where: getWhere({ ...initialValue }, filter),
                            text: freeTextSearch,
                        },
                    };
                });
            }
            dispatch(
                dashboardActions.getDashboardDataMultiple(type, area, queryProp)
            )
                .then((res: any) => {
                    if (res) {
                        let completeDepQuery: any;
                        let depData = Object.keys(res).reduce((accum, key) => {
                            if (queryProp[key].groupBy) {
                                return {
                                    ...accum,
                                    [key]:
                                        res[key].items.length > 0
                                            ? res[key].items.map(
                                                  (value: any) =>
                                                      value[
                                                          queryProp[key]
                                                              .groupBy[0]
                                                      ]
                                              )
                                            : ['none'],
                                };
                            }
                            return accum;
                        }, {});

                        if (
                            depQuery &&
                            res &&
                            res.topCausesOfDeath &&
                            res.topMeansOfDeath
                        ) {
                            setDependentData(depData);
                            if (type === 'activity') {
                                completeDepQuery = depQuery(
                                    filter,
                                    getWhere,
                                    depData
                                );
                            } else {
                                // add dep data as needed to initial filter values of dependent queries
                                completeDepQuery = {};
                                let depQueries = {
                                    ...tabConfigs.dependentQueries,
                                };
                                Object.keys(depQueries).forEach((query) => {
                                    const initialValue =
                                        depQueries[query]['initialFilter'];
                                    let initialCopy = { ...initialValue };
                                    const filterKeys = Object.keys(initialCopy);

                                    // loop through dependency filters to apply arrays to initial filter
                                    for (
                                        let i = 0;
                                        i < filterKeys.length;
                                        i++
                                    ) {
                                        let nestedCopy =
                                            initialCopy[
                                                Object.keys(initialCopy)[i]
                                            ];
                                        let key = filterKeys[i];

                                        nestedCopy = {
                                            ...nestedCopy,
                                            // @ts-ignore
                                            in: depData[
                                                initialValue[key]['in']
                                            ] as any,
                                        };
                                        initialCopy = {
                                            ...initialCopy,
                                            [key]: nestedCopy,
                                        };
                                    }

                                    completeDepQuery = {
                                        ...completeDepQuery,
                                        [query]: {
                                            ...depQueries[query],
                                            where: getWhere(
                                                { ...initialCopy },
                                                filter
                                            ),
                                            text: freeTextSearch,
                                        },
                                    };
                                });
                            }
                            dispatch(
                                dashboardActions.getDashboardDataMultiple(
                                    type,
                                    area,
                                    completeDepQuery
                                )
                            );
                        }
                    }
                })
                .catch((err: any) => {
                    console.log(err);
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const setFilters = useCallback(
        (filters: any) => {
            Object.keys(filters).map((value) => {
                const updatedFilter = {
                    id: value,
                    operator:
                        operatorMap[filters[value].operator] ||
                        filters[value].operator,
                    values: filters[value].values,
                };
                return (filters[value] = updatedFilter);
            });

            dispatch(dashboardActions?.addFilter(filters));
        },
        [dispatch]
    );

    const handleTabChange = useCallback(
        (newTab: React.SetStateAction<number>) => {
            setTabValue(newTab);
        },
        [setTabValue]
    );

    const handleTableData = useCallback(
        (
            id: string,
            fields: string[],
            addCount: boolean,
            groupBy: string,
            limit: number,
            sortAsc: string,
            sortField: string,
            where: any,
            startFrom: string
        ) => {
            if (type === 'activity') {
                fields = [
                    '_id',
                    'userId',
                    'collectionName',
                    'action',
                    'createdAt',
                    'transactionId',
                    'workspaceId',
                ];
            }
            dispatch(
                dashboardActions.getDashboardData(
                    type,
                    id,
                    fields,
                    area,
                    addCount,
                    groupBy,
                    limit,
                    sortAsc,
                    sortField,
                    where,
                    startFrom,
                    freeTextSearch
                )
            );
        },
        [freeTextSearch, type, dispatch, area]
    );

    useEffect(() => {
        if (
            type === 'case' &&
            area &&
            areaConfigs &&
            areaConfigs[area] &&
            areaConfigs[area]['dashboard-configuration'] &&
            areaConfigs[area]['dashboard-configuration'].tabs &&
            Object.keys(areaConfigs[area]['dashboard-configuration'].tabs)
                .length > 0
        ) {
            retrieveData(
                type,
                area,
                areaConfigs[area]['dashboard-configuration'].tabs[tabValue],
                savedFilters || null,
                freeTextSearch
            );
        } else if (
            type === 'activity' &&
            area &&
            dashboardConfig &&
            dashboardConfig.id === area &&
            dashboardConfig.loading === false &&
            dashboardConfig.config
        ) {
            retrieveData(
                type,
                area,
                dashboardConfig.config.tabs[tabValue],
                savedFilters || null,
                freeTextSearch
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area, tabValue, savedFilters, type, dashboardConfig]);

    useEffect(() => {
        // @ts-ignore
        if (type !== 'activity' && area) {
            dispatch(
                workspaceActions?.getWorkspaceConfiguration(
                    area,
                    'dashboard-configuration'
                )
            ).then((data) => {
                if (Object.keys(data).length === 0) {
                    const id = location.pathname.split('/').pop();
                    dispatch(
                        workspaceActions?.setWorkspaceConfiguration(
                            id,
                            'dashboard-configuration',
                            config
                        )
                    );
                }
            });
        } else {
            dispatch(
                dashboardActions.getDashboardConfig(
                    type,
                    area,
                    null,
                    setFilters
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area, type]);

    const onDownloadCsv = useCallback(
        (
            dataType: string,
            queries: any,
            depQueries: any,
            savedFilters: any,
            freeTextSearch: string
        ) => {
            if (dataType === 'activityTable') {
                let fields = [
                    '_id',
                    'collectionName',
                    'userId',
                    'action',
                    'createdAt',
                    'transactionId',
                    'workspaceId',
                ];
                if (
                    currentUser &&
                    currentUser.userConfiguration &&
                    currentUser.userConfiguration.beta
                ) {
                    fields = fields.filter((field) => field !== 'userId');
                }
                dispatch(
                    dashboardActions.exportDashboardData(
                        type.toUpperCase(),
                        area,
                        dataType,
                        {
                            fields,
                            limit: 10,
                            from: 0,
                            sortAsc: false,
                            sortField: 'createdAt',
                            where: getWhere(null, { ...savedFilters }),
                            text: freeTextSearch,
                        }
                    )
                );
                return;
            } else if (depQueries && Object.keys(depQueries).includes(type)) {
                const initialValue = depQueries[dataType]['initialFilter'];
                let initialCopy = { ...initialValue };
                const filterKeys = Object.keys(initialCopy);

                // loop through dependency filters to apply arrays to initial filter
                for (let i = 0; i < filterKeys.length; i++) {
                    let nestedCopy = initialCopy[Object.keys(initialCopy)[i]];
                    const key = filterKeys[i];

                    nestedCopy = {
                        ...nestedCopy,
                        // @ts-ignore
                        in: dependentData[initialValue[key]['in']],
                    };
                    initialCopy = {
                        ...initialCopy,
                        [key]: nestedCopy,
                    };
                }
                const filteredDepQuery = {
                    ...depQueries[dataType],
                    where: getWhere(null, { ...savedFilters }),
                    text: freeTextSearch,
                };
                dispatch(
                    dashboardActions.exportDashboardData(
                        type.toUpperCase(),
                        area,
                        dataType,
                        filteredDepQuery
                    )
                );
            } else {
                const initialValue =
                    queries[dataType] && queries[dataType]['initialFilter'];
                const filteredQuery = {
                    ...queries[dataType],
                    where: getWhere({ ...initialValue }, { ...savedFilters }),
                    text: freeTextSearch,
                };
                dispatch(
                    dashboardActions.exportDashboardData(
                        type.toUpperCase(),
                        area,
                        dataType,
                        filteredQuery
                    )
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [type, dependentData, area]
    );

    const onTableRowClick = useCallback((data: any) => {
        let id = data[0];
        if (id.includes('/')) {
            id = data[0].split('/');
        }
        history.push(`${location.pathname}?id=analytics`);

        dispatch(caseActions?.getCase(area, id));
        history.push(`${location.pathname}?caseId=${id}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (
        (type === 'activity' &&
            (!dashboardConfig ||
                dashboardConfig.id !== area ||
                dashboardConfig.loading)) ||
        (type !== 'activity' &&
            (!areaConfigs[area] ||
                !areaConfigs[area]['dashboard-configuration']))
    ) {
        return null;
    }

    return (
        <div>
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
                {type !== 'activity' && dashboard && (
                    <Grid item xs={12}>
                        <Grid
                            container
                            spacing={1}
                            justifyContent="space-between"
                        >
                            <Grid item xs={12}>
                                {(!view || view === tabs.ANALYTICS) && (
                                    <Grid container>
                                        {areaConfigs &&
                                        areaConfigs[area] &&
                                        areaConfigs[area][
                                            'dashboard-configuration'
                                        ] &&
                                        areaConfigs[area][
                                            'dashboard-configuration'
                                        ].tabs &&
                                        Object.keys(
                                            areaConfigs[area][
                                                'dashboard-configuration'
                                            ].tabs
                                        ).length > 0 ? (
                                            <div
                                                style={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    marginBottom: 60,
                                                    marginTop: -30,
                                                }}
                                            >
                                                <Dashboard
                                                    dashboardData={
                                                        dashboardData as unknown as {
                                                            [key: string]: any;
                                                        }
                                                    }
                                                    dashboardBuild={
                                                        areaConfigs[area][
                                                            'dashboard-configuration'
                                                        ]
                                                    }
                                                    dependentQueries={dependentQueries(
                                                        areaConfigs[area][
                                                            'dashboard-configuration'
                                                        ].tabs[tabValue]
                                                    )}
                                                    savedFilters={savedFilters}
                                                    // @ts-ignore
                                                    tabChange={handleTabChange}
                                                    tabValue={tabValue}
                                                    // @ts-ignore
                                                    exportData={onDownloadCsv}
                                                    onTableRowClick={
                                                        onTableRowClick
                                                    }
                                                    // @ts-ignore
                                                    retrieveDashboardData={
                                                        handleTableData
                                                    }
                                                    setFilters={(f) =>
                                                        setFilters(f)
                                                    }
                                                    mode={theme.palette.mode}
                                                    theme={theme}
                                                    customComponent={
                                                        <Grid
                                                            container
                                                            justifyContent={
                                                                'flex-end'
                                                            }
                                                            spacing={2}
                                                            alignItems={
                                                                'flex-start'
                                                            }
                                                        >
                                                            <Grid
                                                                item
                                                                xs={2}
                                                                lg={5}
                                                                xl={5}
                                                                style={{
                                                                    marginTop: 4,
                                                                }}
                                                            >
                                                                <Filters
                                                                    area={area}
                                                                    type={type}
                                                                    filterType={
                                                                        'simple'
                                                                    }
                                                                    allowFilterTemplates={templateUsers.includes(
                                                                        userRole
                                                                    )}
                                                                />
                                                            </Grid>
                                                            <Grid
                                                                item
                                                                xs={9}
                                                                lg={6}
                                                                xl={6}
                                                            >
                                                                <FreeTextSearch
                                                                    onHandleChange={
                                                                        handleFreeTextSearchChange
                                                                    }
                                                                    onHandleQuickSearch={
                                                                        handleQuickSearch
                                                                    }
                                                                    value={
                                                                        freeTextSearch
                                                                    }
                                                                />
                                                            </Grid>
                                                            <Grid item xs={1}>
                                                                <Tooltip
                                                                    title={
                                                                        'Download Analytics as PDF'
                                                                    }
                                                                >
                                                                    <IconButton
                                                                        style={{
                                                                            padding: 2,
                                                                            borderRadius: 5,
                                                                        }}
                                                                        onClick={() =>
                                                                            dispatch(
                                                                                dashboardActions.exportDashboard(
                                                                                    type.toUpperCase(),
                                                                                    area,
                                                                                    areaConfigs[
                                                                                        area
                                                                                    ][
                                                                                        'dashboard-configuration'
                                                                                    ],
                                                                                    savedFilters ||
                                                                                        {},
                                                                                    freeTextSearch
                                                                                )
                                                                            )
                                                                        }
                                                                    >
                                                                        <img
                                                                            src={
                                                                                lightMode
                                                                                    ? ExportPDFIcon
                                                                                    : ExportPDFIconDarkMode
                                                                            }
                                                                            alt="PDF Download Logo"
                                                                            height={
                                                                                35
                                                                            }
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Grid>
                                                        </Grid>
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <Grid
                                                container
                                                justifyContent={'center'}
                                                direction={'column'}
                                                alignItems={'center'}
                                                alignContent={'center'}
                                            >
                                                <Grid item>
                                                    <Typography
                                                        variant={'subtitle1'}
                                                        gutterBottom
                                                        style={{
                                                            marginTop: 50,
                                                        }}
                                                    >
                                                        There are currently no
                                                        configured dashboards
                                                        for this area
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                {/*@ts-ignore*/}
                {type === 'activity' && (
                    <Grid item xs={11}>
                        <Grid
                            container
                            spacing={1}
                            justifyContent="space-between"
                        >
                            <Dashboard
                                dashboardData={dashboard}
                                dashboardBuild={dashboardConfig.config}
                                // @ts-ignore
                                // dependentQueries={dependentQueries}
                                savedFilters={savedFilters}
                                // @ts-ignore
                                tabChange={handleTabChange}
                                tabValue={tabValue}
                                // @ts-ignore
                                exportData={onDownloadCsv}
                                // @ts-ignore
                                retrieveDashboardData={handleTableData}
                                setFilters={setFilters}
                                mode={theme.palette.mode}
                                theme={theme}
                            />
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </div>
    );
};

export default DashboardWrapper;
